import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Link,
  Stack,
  Typography,
  Container,
  Grid2,
} from "@mui/material";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import COLORS from "../../constants/colors";
import ArrowDownChevron from "../icons/ArrowDownChevron";
import { StandardConfig } from "../sanity/portableText/configurations";
import useIsMobile from "../../hooks/useIsMobile";

interface Span {
  _key: string;
  _type: string;
  marks: string[];
  text: string;
}

interface Block {
  _key: string;
  _type: string;
  children: Span[];
  markDefs: any[];
  style: string;
}

interface Question {
  question: string;
  answer: Block[];
}

interface IFaqContainerProps {
  headerText?: string;
  headerColor?: string;
  questionColor?: string;
  contactPromptBgColor?: string;
  contactPrompt?: boolean;
  contactMail?: string;
  items?: Question[];
}

const linkMappings: { [key: string]: string } = {
  appen: "/app",
  "Horde-appen": "/app",
  livestreamen: "/gjeldfri/stot",
  "horde.no/gjeldfri/stot": "https://horde.no/gjeldfri/stot",
  "Se vilkår": "/gjeldfri/bilkampanje-vilkaar",
};
const replaceLinks = (text: string | undefined): React.ReactNode => {
  if (!text) {
    return null;
  }
  const parts = text.split(/(\[.*?\])/g);
  return parts.map((part, index) => {
    if (part.startsWith("[") && part.endsWith("]")) {
      const linkText = part.slice(1, -1);
      const href = linkMappings[linkText] || `#${linkText}`;
      return (
        <Link
          href={href}
          key={index}
          target={href.startsWith("http") ? "_blank" : "_self"}
          rel="noreferrer"
        >
          {linkText}
        </Link>
      );
    }
    return part;
  });
};

const customComponents: PortableTextComponents = {
  ...StandardConfig,
  block: {
    normal: ({ children }) => (
      <Typography variant="body1">{children}</Typography>
    ),
  },
  marks: {
    link: ({ children, value }) => {
      const href = linkMappings[value.href] || value.href;
      return (
        <Link href={href} target="_self" rel="noreferrer">
          {children}
        </Link>
      );
    },
  },
  types: {
    block: ({ value }) => {
      return (
        <Typography variant="body1" color={COLORS.BLACK[100]}>
          {value.children.map((child: Span) => replaceLinks(child.text))}
        </Typography>
      );
    },
  },
};

const InlineFaq: React.FC<IFaqContainerProps> = ({
  headerText,
  questionColor = COLORS.BLUE[100],
  headerColor = COLORS.BLUE[100],
  contactPrompt = false,
  contactPromptBgColor = COLORS.DARK_BLUE[100],
  contactMail = "support@horde.no",
  items,
}: IFaqContainerProps) => {
  const isMobile = useIsMobile();
  return (
    <Stack spacing="48px">
      <Stack spacing="48px">
        {headerText && (
          <Typography
            textAlign="center"
            variant={isMobile ? "ambassadorTitle" : "h2"}
            color={headerColor}
          >
            {headerText}
          </Typography>
        )}
        <Stack spacing="22px">
          {items?.map((item, index) => {
            return (
              <Container key={index}>
                <Accordion
                  key={index}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: "12px !important",
                    overflow: "hidden",
                    border: "none",
                  }}
                >
                  <AccordionSummary
                    sx={{
                      backgroundColor: COLORS.TRUE_WHITE[100],
                      display: "flex",
                      flexDirection: "row",
                    }}
                    expandIcon={
                      <ArrowDownChevron
                        stroke={COLORS.BLUE[100]}
                        sx={{
                          width: "15px",
                          height: "15px",
                        }}
                      />
                    }
                  >
                    <Box pr="8px" my="-16px">
                      <Typography
                        color={questionColor}
                        variant="lead"
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.question}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box
                      p="4px 50px 36px 36px"
                      bgcolor={COLORS.TRUE_WHITE[100]}
                    >
                      <PortableText
                        value={item.answer}
                        components={customComponents}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Container>
            );
          })}
        </Stack>
      </Stack>
      {contactPrompt && (
        <Grid2 container alignItems="center" justifyContent="center">
          <Grid2 size={12} paddingX="22px">
            <Box
              bgcolor={contactPromptBgColor}
              py="28px"
              px="40px"
              borderRadius="20px"
            >
              <Stack spacing="16px" justifyContent="center" alignItems="center">
                <Typography
                  variant="h4"
                  color={COLORS.WHITE[100]}
                  textAlign="center"
                >
                  Lurer du på noe annet?
                </Typography>
                <Typography
                  variant="bodySmall"
                  color={COLORS.WHITE[100]}
                  textAlign="center"
                >
                  Send oss en e-post på
                  <Link
                    href={`mailto:${contactMail}`}
                    target="_blank"
                    rel="noreferrer"
                    color="secondary"
                  >
                    {contactMail}
                  </Link>
                </Typography>
              </Stack>
            </Box>
          </Grid2>
        </Grid2>
      )}
    </Stack>
  );
};

export default InlineFaq;
